import React, { useState, useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

// ============================================================================
// TIME FILTER OPTIONS
// ============================================================================

const TIME_FILTERS = [
  { id: "1D", label: "1D", days: 1 },
  { id: "3D", label: "3D", days: 3 },
  { id: "5D", label: "5D", days: 5 },
  { id: "1W", label: "1W", days: 7 },
  { id: "1M", label: "1M", days: 30 },
  { id: "3M", label: "3M", days: 90 },
  { id: "1Y", label: "1Y", days: 365 },
  { id: "ALL", label: "ALL", days: Infinity },
];

// ============================================================================
// CUSTOM TOOLTIP
// ============================================================================

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const value = payload[0].value;
  const isPositive = value >= 0;

  return (
    <div
      style={{
        backgroundColor: "#1a1a1a",
        border: "1px solid #333",
        borderRadius: "6px",
        padding: "10px 14px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
      }}
    >
      <div style={{ color: "#888", fontSize: "11px", marginBottom: "4px" }}>
        {label}
      </div>
      <div
        style={{
          color: isPositive ? "#60ffd0" : "#ff6b6b",
          fontSize: "16px",
          fontWeight: "bold",
        }}
      >
        {isPositive ? "+" : ""}
        ${value.toLocaleString()}
      </div>
    </div>
  );
};

// ============================================================================
// PROFIT CHART COMPONENT
// ============================================================================

export default function ProfitChart({ financialHistory = [], currentDay = 1 }) {
  const [activeFilter, setActiveFilter] = useState("1W");

  // Filter data based on selected time range
  const filteredData = useMemo(() => {
    const filter = TIME_FILTERS.find((f) => f.id === activeFilter);
    if (!filter) return financialHistory;

    if (filter.days === Infinity) {
      return financialHistory;
    }

    // Get last N days of data
    const cutoffDay = Math.max(1, currentDay - filter.days);
    return financialHistory.filter((d) => d.day >= cutoffDay);
  }, [financialHistory, activeFilter, currentDay]);

  // Calculate trend (is overall positive or negative?)
  const trend = useMemo(() => {
    if (filteredData.length < 2) return "neutral";
    const first = filteredData[0]?.netProfit || 0;
    const last = filteredData[filteredData.length - 1]?.netProfit || 0;
    return last >= first ? "up" : "down";
  }, [filteredData]);

  // Calculate summary stats
  const stats = useMemo(() => {
    if (filteredData.length === 0) {
      return { total: 0, avg: 0, min: 0, max: 0 };
    }
    const profits = filteredData.map((d) => d.netProfit || 0);
    const total = profits.reduce((a, b) => a + b, 0);
    return {
      total,
      avg: Math.round(total / profits.length),
      min: Math.min(...profits),
      max: Math.max(...profits),
    };
  }, [filteredData]);

  // Colors based on trend
  const colors = {
    up: {
      stroke: "#60ffd0",
      gradientStart: "rgba(96, 255, 208, 0.4)",
      gradientEnd: "rgba(96, 255, 208, 0.0)",
    },
    down: {
      stroke: "#ff6b6b",
      gradientStart: "rgba(255, 107, 107, 0.4)",
      gradientEnd: "rgba(255, 107, 107, 0.0)",
    },
    neutral: {
      stroke: "#888",
      gradientStart: "rgba(136, 136, 136, 0.4)",
      gradientEnd: "rgba(136, 136, 136, 0.0)",
    },
  };

  const activeColors = colors[trend];

  // Format data for chart
  const chartData = filteredData.map((d) => ({
    name: `Day ${d.day}`,
    profit: d.netProfit || 0,
    date: d.date || `Day ${d.day}`,
  }));

  // If no data, show placeholder
  if (financialHistory.length === 0) {
    return (
      <div
        style={{
          backgroundColor: "#0c0c0c",
          border: "1px solid #1a1a1a",
          borderRadius: "8px",
          padding: "16px",
          paddingLeft: "180px",
          height: "300px",
          maxHeight: "300px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h3 style={{ color: "#60ffd0", fontSize: "14px", margin: 0 }}>
            Profit History
          </h3>
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#555",
            fontSize: "14px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "16px", marginBottom: "8px", color: "#666" }}>---</div>
            <div>No financial history yet</div>
            <div style={{ fontSize: "12px", color: "#444", marginTop: "4px" }}>
              Complete flights to see profit trends
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "#0c0c0c",
        border: "1px solid #1a1a1a",
        borderRadius: "8px",
        padding: "16px",
        paddingLeft: "180px",
        height: "350px",
        maxHeight: "350px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header with filters */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <h3 style={{ color: "#60ffd0", fontSize: "14px", margin: 0 }}>
          Profit History
        </h3>

        {/* Time filter tabs */}
        <div style={{ display: "flex", gap: "4px" }}>
          {TIME_FILTERS.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              style={{
                padding: "4px 10px",
                fontSize: "11px",
                fontWeight: activeFilter === filter.id ? "bold" : "normal",
                backgroundColor:
                  activeFilter === filter.id ? "#1a3a3a" : "transparent",
                color: activeFilter === filter.id ? "#60ffd0" : "#666",
                border:
                  activeFilter === filter.id
                    ? "1px solid #60ffd0"
                    : "1px solid #333",
                borderRadius: "4px",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary stats row */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "12px",
          fontSize: "12px",
        }}
      >
        <div>
          <span style={{ color: "#666" }}>Period Total: </span>
          <span
            style={{
              color: stats.total >= 0 ? "#60ffd0" : "#ff6b6b",
              fontWeight: "bold",
            }}
          >
            {stats.total >= 0 ? "+" : ""}${stats.total.toLocaleString()}
          </span>
        </div>
        <div>
          <span style={{ color: "#666" }}>Avg/Day: </span>
          <span
            style={{
              color: stats.avg >= 0 ? "#60ffd0" : "#ff6b6b",
            }}
          >
            {stats.avg >= 0 ? "+" : ""}${stats.avg.toLocaleString()}
          </span>
        </div>
        <div>
          <span style={{ color: "#666" }}>Best: </span>
          <span style={{ color: "#60ffd0" }}>
            +${Math.max(0, stats.max).toLocaleString()}
          </span>
        </div>
        <div>
          <span style={{ color: "#666" }}>Worst: </span>
          <span style={{ color: stats.min < 0 ? "#ff6b6b" : "#888" }}>
            ${stats.min.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Chart */}
      <div style={{ flex: 1, minHeight: "200px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={activeColors.gradientStart} />
                <stop offset="100%" stopColor={activeColors.gradientEnd} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#555", fontSize: 10 }}
              interval="preserveStartEnd"
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#555", fontSize: 10 }}
              tickFormatter={(v) =>
                v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`
              }
              width={50}
            />

            {/* Zero reference line */}
            <ReferenceLine
              y={0}
              stroke="#333"
              strokeDasharray="3 3"
            />

            <Tooltip content={<CustomTooltip />} />

            <Area
              type="monotone"
              dataKey="profit"
              stroke={activeColors.stroke}
              strokeWidth={2}
              fill="url(#profitGradient)"
              animationDuration={300}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}